import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSubscription } from '@/context/SubscriptionContext';
import { useAuth } from '@/context/AuthContext';
import { PricingCard } from '@/components/pricing/PricingCard';
import { BillingToggle } from '@/components/pricing/BillingToggle';
import { FeatureComparisonTable, PricingFAQ } from '@/components/pricing';
import { PRICING_TIERS } from '@/components/pricing/constants';
import { BillingCycle } from '@/components/pricing/types';
import { toast } from '@/hooks/ui/use-toast';

const SelectPlan: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { createSubscription, subscription, isLoading: subscriptionLoading } = useSubscription();

  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Check for pre-selected plan from URL or canceled checkout
  useEffect(() => {
    const planFromUrl = searchParams.get('plan');
    if (planFromUrl && PRICING_TIERS.some(t => t.id === planFromUrl)) {
      setSelectedPlan(planFromUrl);
    }

    // Handle canceled checkout
    if (searchParams.get('subscription') === 'canceled') {
      toast.error("Checkout canceled", {
        description: "Your payment was not completed. Please select a plan to continue."
      });
      // Clean up URL
      window.history.replaceState({}, '', '/select-plan');
    }
  }, [searchParams]);

  // If user already has valid subscription, redirect to create-business or dashboard
  useEffect(() => {
    if (!subscriptionLoading && subscription && ['active', 'trialing'].includes(subscription.status)) {
      navigate('/create-business', { replace: true });
    }
  }, [subscription, subscriptionLoading, navigate]);

  const handleSelectPlan = async (planId: string) => {
    if (planId === 'enterprise') {
      window.location.href = '/contact?plan=enterprise';
      return;
    }

    setSelectedPlan(planId);
    setIsProcessing(true);

    try {
      const checkoutUrl = await createSubscription(planId, billingCycle);

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        toast.error("Failed to start checkout", {
          description: "Please try again or contact support."
        });
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error("Something went wrong", {
        description: "Please try again or contact support."
      });
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-background">
      <main className="flex-grow">
        {/* Hero Section - Modified for onboarding */}
        <section className="pt-16 pb-8 sm:pt-24 sm:pb-12 relative overflow-hidden">
          {/* Multi-layered gradient background */}
          <div className="absolute inset-0 -z-10">
            <div
              className="absolute inset-0"
              style={{
                background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(var(--primary-rgb), 0.12), transparent)',
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'radial-gradient(circle at 90% 30%, rgba(var(--grow-rgb), 0.08), transparent 50%)',
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'radial-gradient(circle at 10% 70%, rgba(var(--secondary-rgb), 0.06), transparent 50%)',
              }}
            />
            <div className="absolute inset-0 bento-noise opacity-[0.02]" />
          </div>

          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6"
            >
              Choose Your
              <br />
              <span className="bg-gradient-to-r from-primary via-grow to-secondary bg-clip-text text-transparent">
                Perfect Plan
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed"
            >
              Start with a 14-day free trial. Your card won't be charged until the trial ends.
              <span className="block mt-1 text-foreground font-medium">
                Cancel anytime during your trial.
              </span>
            </motion.p>
          </div>
        </section>

        {/* Pricing Cards with Billing Toggle */}
        <section className="py-8 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Billing Toggle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex justify-center mb-12"
            >
              <div className="inline-flex items-center bg-muted/50 backdrop-blur-sm rounded-full px-6 py-3 border border-border/50">
                <BillingToggle
                  billingCycle={billingCycle}
                  onToggle={setBillingCycle}
                />
              </div>
            </motion.div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-4 xl:gap-6 items-stretch">
              {PRICING_TIERS.map((tier, index) => (
                <PricingCard
                  key={tier.id}
                  tier={tier}
                  billingCycle={billingCycle}
                  index={index}
                  onSelect={handleSelectPlan}
                  isSelected={selectedPlan === tier.id}
                  isLoading={isProcessing}
                />
              ))}
            </div>

            {/* Trust note */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="text-center text-sm text-muted-foreground mt-12"
            >
              All plans include secure data encryption, automatic backups, and GDPR compliance.
              <br className="hidden sm:block" />
              {' '}You can upgrade, downgrade, or cancel anytime.
            </motion.p>
          </div>
        </section>

        {/* Feature Comparison Table */}
        <FeatureComparisonTable />

        {/* FAQ Section */}
        <PricingFAQ />
      </main>
    </div>
  );
};

export default SelectPlan;
